import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BUDGET_CATEGORIES } from "@/lib/categories";
import { currentMonth, monthLabel, formatDate } from "@/lib/helpers";
import { Loader2, Plus, Trash2, Wallet, TrendingDown, PiggyBank } from "lucide-react";
import { toast } from "sonner";

export default function Budget() {
  const [month, setMonth] = useState(currentMonth());
  const [months, setMonths] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetAmount, setBudgetAmount] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);

  const [txAmount, setTxAmount] = useState("");
  const [txCategory, setTxCategory] = useState("groceries");
  const [txNote, setTxNote] = useState("");
  const [addingTx, setAddingTx] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [budgetsRes, currRes] = await Promise.all([
        api.get("/budget"),
        api.get(`/budget/${month}`),
      ]);
      setMonths(budgetsRes.data.months);
      setData(currRes.data);
      if (!budgetsRes.data.months.find((m) => m.month === month)) {
        setBudgetAmount("");
      }
    } catch {
      toast.error("Could not load budget");
    }
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const saveBudget = async (e) => {
    e.preventDefault();
    const amt = parseFloat(budgetAmount);
    if (!amt || amt <= 0) return;
    setSavingBudget(true);
    try {
      await api.post("/budget", { month, amount: amt });
      toast.success("Budget set!");
      setBudgetAmount("");
      load();
    } catch {
      toast.error("Could not save budget");
    }
    setSavingBudget(false);
  };

  const addTx = async (e) => {
    e.preventDefault();
    const amt = parseFloat(txAmount);
    if (!amt || amt <= 0) return;
    setAddingTx(true);
    try {
      await api.post("/budget/transaction", { month, amount: amt, category: txCategory, note: txNote });
      setTxAmount("");
      setTxNote("");
      load();
    } catch {
      toast.error("Could not add expense");
    }
    setAddingTx(false);
  };

  const deleteTx = async (id) => {
    try {
      await api.delete(`/budget/transaction/${id}`);
      load();
    } catch {}
  };

  const hasBudget = data && data.budget > 0;
  const percentRemaining = hasBudget ? Math.max(0, Math.min(100, (data.remaining / data.budget) * 100)) : 0;
  const barColor = percentRemaining > 50 ? "#2A9D8F" : percentRemaining > 25 ? "#F4A261" : "#E76F51";

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold text-[#2A9D8F] tracking-widest uppercase">Toolbox</p>
        <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-bold text-[#1B2A4A] leading-tight">
          Simple <span className="text-[#E8621A]">Budget.</span>
        </h1>
        <p className="mt-3 text-[#1B2A4A]/70 max-w-xl">
          Set a monthly budget. Log what you spend. See where the month is heading — no spreadsheets, no stress.
        </p>
      </div>

      {/* Month picker */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-sm font-semibold text-[#1B2A4A]">Month:</Label>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger data-testid="budget-month-select" className="w-56 h-11 rounded-full bg-white border-[#1B2A4A]/15">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-2xl">
            <SelectItem value={currentMonth()}>{monthLabel(currentMonth())} (Current)</SelectItem>
            {months.filter((m) => m.month !== currentMonth()).map((m) => (
              <SelectItem key={m.month} value={m.month}>{monthLabel(m.month)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#E8621A]" /></div>
      ) : !hasBudget ? (
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={saveBudget}
          data-testid="budget-set-form"
          className="bg-white rounded-3xl border border-[#1B2A4A]/8 p-6 sm:p-8 max-w-md"
        >
          <div className="w-12 h-12 rounded-2xl bg-[#E8621A]/10 flex items-center justify-center mb-4">
            <Wallet className="w-6 h-6 text-[#E8621A]" />
          </div>
          <h2 className="font-heading font-bold text-2xl text-[#1B2A4A]">Set your budget for {monthLabel(month)}</h2>
          <p className="text-sm text-[#1B2A4A]/60 mt-1 mb-6">What do you want to keep spending within?</p>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-[#1B2A4A]">Total budget ($)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              required
              value={budgetAmount}
              data-testid="budget-amount-input"
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder="e.g., 1500"
              className="h-12 rounded-xl bg-white border-[#1B2A4A]/15 focus-visible:ring-2 focus-visible:ring-[#E8621A]/50 text-lg font-semibold"
            />
          </div>
          <Button type="submit" disabled={savingBudget} data-testid="budget-set-submit" className="w-full mt-6 h-12 rounded-full bg-[#E8621A] hover:bg-[#D05615]">
            {savingBudget ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set budget"}
          </Button>
        </motion.form>
      ) : (
        <>
          {/* Hero stat */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-3xl bg-[#1B2A4A] text-white p-6 sm:p-8"
          >
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#E8621A]/25 blur-3xl" />
            <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-[#2A9D8F]/25 blur-3xl" />
            <div className="relative">
              <p className="text-xs font-semibold tracking-widest uppercase text-white/60">Remaining · {monthLabel(month)}</p>
              <p data-testid="budget-remaining" className="mt-2 font-heading text-5xl sm:text-6xl font-bold">
                ${data.remaining.toFixed(2)}
              </p>
              <p className="mt-2 text-sm text-white/70">
                <span className="text-white font-semibold">${data.spent.toFixed(2)}</span> spent of <span className="text-white font-semibold">${data.budget.toFixed(2)}</span>
              </p>

              <div className="mt-6 h-3 w-full rounded-full bg-white/15 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentRemaining}%` }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="h-full rounded-full"
                  style={{ background: barColor }}
                  data-testid="budget-progress-bar"
                />
              </div>
              <p className="mt-2 text-xs text-white/60">
                {percentRemaining > 50 ? "You're pacing well 🌱" : percentRemaining > 25 ? "Steady — watch closely 🍂" : "Cutting it close 🚨"}
              </p>
            </div>
          </motion.div>

          {/* Add expense */}
          <form onSubmit={addTx} className="bg-white rounded-3xl border border-[#1B2A4A]/8 p-5 sm:p-6" data-testid="expense-form">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#E76F51]/10 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-[#E76F51]" />
              </div>
              <p className="font-heading font-bold text-lg text-[#1B2A4A]">Log an expense</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5 sm:col-span-1">
                <Label className="text-xs font-semibold text-[#1B2A4A]/70">Amount ($)</Label>
                <Input
                  type="number" step="0.01" min="0.01" required
                  value={txAmount}
                  onChange={(e) => setTxAmount(e.target.value)}
                  data-testid="expense-amount"
                  placeholder="24.99"
                  className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-1">
                <Label className="text-xs font-semibold text-[#1B2A4A]/70">Category</Label>
                <Select value={txCategory} onValueChange={setTxCategory}>
                  <SelectTrigger data-testid="expense-category" className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-2xl">
                    {BUDGET_CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-semibold text-[#1B2A4A]/70">Note (optional)</Label>
                <Input
                  value={txNote}
                  onChange={(e) => setTxNote(e.target.value)}
                  data-testid="expense-note"
                  placeholder="e.g., weekly groceries at Aldi"
                  className="h-11 rounded-xl bg-white border-[#1B2A4A]/15"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button type="submit" disabled={addingTx} data-testid="expense-submit" className="rounded-full bg-[#E8621A] hover:bg-[#D05615] px-6">
                {addingTx ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Plus className="w-4 h-4 mr-1" /> Add expense</>)}
              </Button>
            </div>
          </form>

          {/* Transactions list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-lg text-[#1B2A4A]">Transactions</h3>
              <p className="text-xs text-[#1B2A4A]/50">{data.transactions.length} entries</p>
            </div>
            {data.transactions.length === 0 ? (
              <div className="bg-white rounded-3xl border border-dashed border-[#1B2A4A]/15 text-center py-10">
                <PiggyBank className="w-8 h-8 mx-auto text-[#1B2A4A]/30" />
                <p className="mt-2 text-sm text-[#1B2A4A]/60">No expenses yet — start logging above.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {data.transactions.map((t) => {
                  const cat = BUDGET_CATEGORIES.find((c) => c.id === t.category) || { label: t.category };
                  return (
                    <li
                      key={t.id}
                      data-testid={`txn-${t.id}`}
                      className="flex items-center gap-3 bg-white rounded-2xl border border-[#1B2A4A]/8 px-4 py-3"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#1B2A4A] truncate">
                          {t.note || cat.label}
                        </p>
                        <p className="text-xs text-[#1B2A4A]/60">{cat.label} · {formatDate(t.created_at)}</p>
                      </div>
                      <p className="font-bold text-[#1B2A4A]">${t.amount.toFixed(2)}</p>
                      <button
                        onClick={() => deleteTx(t.id)}
                        data-testid={`txn-delete-${t.id}`}
                        className="p-2 rounded-full text-[#1B2A4A]/40 hover:text-[#E76F51] hover:bg-[#E76F51]/8 transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
